import React from "react";
import WarningRoundedIcon from "@material-ui/icons/WarningRounded";
import TextInput from "../TextInput/TextInput";
import { acopladosQueQuedanSolos, VehicleInsideRow } from "../Table/utils/vehicleInsideRow";

type TractorRetiraFieldProps = {
  value: string;
  onChange: (value: string) => void;
  error?: string;
  /** Tractores que se pueden sugerir con un toque. Vacío ⇒ sólo carga manual de la patente. */
  candidates: VehicleInsideRow[];
  /** Patentes que ya salen en esta misma salida, para no avisar por unidades que igual se van. */
  selectedPlates: string[];
};

/**
 * Bloque "tractor que retira el acoplado", compartido por los dos modales de salida.
 *
 * Es presentacional a propósito: la patente y su error viven en cada modal, que son los que saben
 * cuándo la regla aplica (`requiereTractor`) y qué tractores tienen para sugerir.
 */
const TractorRetiraField: React.FC<TractorRetiraFieldProps> = ({
  value,
  onChange,
  error,
  candidates,
  selectedPlates,
}) => {
  const tractorDentro = candidates.find(
    (other) => (other.plate ?? "").toUpperCase() === value.trim().toUpperCase()
  );

  // Si el tractor elegido deja sus propios acoplados adentro, se avisa antes de confirmar.
  const huerfanos = acopladosQueQuedanSolos(tractorDentro, selectedPlates);

  return (
    <div className="ve-sub">
      <h4 className="ve-block-title">
        Tractor que retira el acoplado <span className="ve-req">*</span>
      </h4>
      <p className="ve-hint">
        Un acoplado no puede salir sin tractor.{" "}
        {candidates.length > 0 && "Elegí uno que esté dentro del complejo o "}
        {candidates.length > 0 ? "cargá" : "Cargá"} la patente del que lo viene a buscar.
      </p>
      <TextInput
        value={value}
        onChange={(next) => onChange(next.toUpperCase())}
        extraType="plate"
        placeholder="Ej. ASD3212"
        error={error}
      />
      {candidates.length > 0 && (
        <div className="ve-sugeridos">
          {candidates.map((tractor) => (
            <button
              key={tractor.key}
              type="button"
              className="ve-sugerido"
              onClick={() => onChange((tractor.plate ?? "").toUpperCase())}
            >
              {tractor.plate}
            </button>
          ))}
        </div>
      )}
      {value.trim() && (
        <p className="ve-hint">
          {tractorDentro
            ? `${tractorDentro.plate} está dentro del complejo. Al confirmar también se registra su salida.`
            : "No hay ninguna unidad dentro con esa patente: se registra como tractor externo que entra a buscar el acoplado."}
        </p>
      )}
      {huerfanos.length > 0 && (
        <div className="ve-alert">
          <WarningRoundedIcon fontSize="small" />
          <div>
            <b>
              {tractorDentro?.plate} tiene {huerfanos.join(", ")} enganchado
              {huerfanos.length > 1 ? "s" : ""}.
            </b>
            <br />
            Si sale con este acoplado, {huerfanos.length > 1 ? "quedan" : "queda"} dentro sin
            tractor.
          </div>
        </div>
      )}
    </div>
  );
};

export default TractorRetiraField;
